import React, { useState, useContext } from 'react'
import classnames from 'classnames'

// App Context
import { AppContext } from '../../../../../../contexts'

// Hooks
import { useMarketTempIndicator, useFullListingData } from '../../../../../../hooks/listing'
import { useLogGAEvent } from '../../../../../../hooks/googleAnalytics'

// Constants
import { GREEN, ORANGE } from '../../../../../../constants/indicators'
import { LISTING_EVAL_SLIDER_MOVED } from '../../../../../../constants/googleAnalytics'

// Components
import { CircleIcon, Text, Panel, PanelDivider, Slider } from '../../../../../../components'
import { LightBulb } from '../../../../../../components/Svg'

// helpers
import { formatToCurrency, formatAbbrCurrency } from '../../../../../../helpers'
import { roundWithPrecision } from '../../../../../../helpers/format'
import {
  buildSliderValues,
  hijackSliderValues,
} from '../../../../../../helpers/sliderHelpers'

// Asserts
import { HomebotIcon } from '../../../../../../components/Svg'

// Styles
import baseStyles from '../base.module.scss'

const MarketTemp = () => {
  const { marketTempSliderPrice, setMarketTempSliderPrice } =
    useContext(AppContext)
  const indicator = useMarketTempIndicator()
  const listing = useFullListingData()
  const logGA = useLogGAEvent(LISTING_EVAL_SLIDER_MOVED, {
    debounceInterval: 5000,
  })

  const { listingPrice } = listing

  const [sliderValue, setSliderValue] = useState(
    marketTempSliderPrice || listingPrice
  )

  if (!indicator) {
    return null
  }

  const { color: status, theme, marketTemp } = indicator

  const botHeadValue = () => {
    const roundTo = listingPrice + listingPrice * 0.05 >= 1000000 ? -4 : -3

    if (marketTemp >= 60)
      return roundWithPrecision(listingPrice + listingPrice * 0.05, roundTo)
    if (marketTemp >= 40) return roundWithPrecision(listingPrice, roundTo)
    if (marketTemp < 40)
      return roundWithPrecision(listingPrice - listingPrice * 0.05, roundTo)
  }

  const roundedBotheadValue = Math.round(botHeadValue())

  const copy = {
    [GREEN]: {
      title: marketTemp >= 40 ? 'BALANCED MARKET' : 'COOL MARKET',
      sub:
        marketTemp >= 40
          ? 'Houses are selling for near list price.'
          : 'You could come in under the list price.',
      icon: marketTemp >= 40 ? 'check' : 'snowflakeCool',
      color: GREEN,
    },
    [ORANGE]: {
      title: 'IN DEMAND MARKET',
      sub: 'There’s likely to be multiple offers.',
      icon: 'chart',
      color: ORANGE,
    },
  }

  const FooterText = () => {
    if (marketTemp >= 60) {
      return (
        <Text size='s'>
          Since competition is high &#38; homes tend to sell over listing price,
          we think you might need to offer{' '}
          <Text element='span' color='white' weight='semibold'>
            &#126;{formatAbbrCurrency(listingPrice + listingPrice * 0.05)}
          </Text>
        </Text>
      )
    } else if (marketTemp >= 40) {
      return (
        <Text size='s'>
          Since competition is balanced &#38; homes are selling near listing
          price, we think you could offer{' '}
          <Text element='span' color='white' weight='semibold'>
            &#126;{formatAbbrCurrency(listingPrice)}
          </Text>
        </Text>
      )
    } else {
      return (
        <Text size='s'>
          Since competition is low &#38; homes are selling under listing price,
          we think you could offer{' '}
          <Text element='span' color='white' weight='semibold'>
            &#126;{formatAbbrCurrency(listingPrice - listingPrice * 0.05)}
          </Text>
        </Text>
      )
    }
  }

  const { title, sub, icon, color } = copy[status]

  const BotHead = () => {
    return (
      <div className={baseStyles.hbHead}>
        <HomebotIcon />
      </div>
    )
  }

  const PriceText = () => {
    return (
      <Text size='s' weight='bold'>
        {formatToCurrency(Math.abs(listingPrice))} price
      </Text>
    )
  }

  const markers = [
    {
      value: roundedBotheadValue,
      direction: 'down',
      content: <BotHead />,
    },
    {
      value: listingPrice,
      direction: 'up',
      content: <PriceText />,
    },
  ]

  const step = 1
  const interval = listingPrice >= 1000000 ? 10000 : 5000

  const min =
    Math.floor((listingPrice - listingPrice * 0.3) / interval) * interval
  const max =
    Math.ceil((listingPrice + listingPrice * 0.3) / interval) * interval

  const sliderValues = buildSliderValues({
    price: listingPrice,
    min,
    max,
    botHeadValue: roundedBotheadValue,
  })

  const onSliderChange = e => {
    const num = parseInt(e.target.valueAsNumber)

    const requestValue = hijackSliderValues({
      markerValues: [listingPrice, roundedBotheadValue],
      allSpecialValues: sliderValues,
      interval,
      sliderValue: num,
      roundValue: true,
    })

    setSliderValue(requestValue)
    setMarketTempSliderPrice(requestValue)

    logGA({
      slider_name: 'Custom Listing Price',
      slider_value: sliderValue,
    })
  }

  return (
    <Panel className={baseStyles.panel}>
      <div className={baseStyles.label}>
        <CircleIcon
          className={baseStyles.icon}
          backgroundColor={theme}
          icon={icon}
        />
        <Text
          size='s'
          letterSpacing='large'
          color={color}
          weight='bold'
          className={classnames(baseStyles.heading, baseStyles.center)}
        >
          {title}
        </Text>
      </div>
      <Text size='m' color='white'>
        {sub}
      </Text>
      <Text size='m' color='white'>
        What would you offer?
      </Text>
      <Text
        size='xxxl'
        color='eggShell'
        weight='light'
        className={baseStyles.amount}
      >
        {formatToCurrency(Math.abs(sliderValue))}
      </Text>
      <Slider
        name={'marketTempSlider'}
        min={min}
        max={max}
        step={step}
        minPhrase={formatAbbrCurrency(Math.abs(min)).toString()}
        maxPhrase={formatAbbrCurrency(Math.abs(max)).toString()}
        value={sliderValue}
        onChange={onSliderChange}
        markers={markers}
        className={baseStyles.slider}
      />
      <PanelDivider footer icon={LightBulb}>
        <FooterText />
      </PanelDivider>
    </Panel>
  )
}

export default MarketTemp
